import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { surveysApi, type QResult, type ResponseRow, type SurveyStatus } from '../features/surveys/api.ts';

const STATUS_LABEL: Record<SurveyStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
};

// Submitted timestamps are shown in Maldives time (UTC+5, no DST) so admins see
// the same wall-clock the enumerator collected at, wherever they review from.
function fmtMvt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Indian/Maldives',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// A short, type-agnostic preview of a response's answers for the list row.
function previewAnswers(answers: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const v of Object.values(answers)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      parts.push(v.map((x) => String(x)).join(', '));
    } else if (typeof v === 'object') {
      parts.push(Object.entries(v as Record<string, unknown>).map(([k, n]) => `${k}: ${String(n)}`).join(', '));
    } else {
      parts.push(String(v));
    }
    if (parts.length >= 2) break;
  }
  return parts.join(' · ');
}

// Themed tooltip — replaces recharts' hardcoded white box so it reads on the
// paper surface in both light and dark mode. Minimal local props type (recharts
// injects active/payload/label at render time).
interface TooltipBarProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ value?: number | string }>;
}
function ChartTooltip({ active, payload, label }: TooltipBarProps): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const raw = payload[0]?.value;
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return (
    <div className="card-paper px-3 py-2 shadow-lg">
      <p className="text-sm font-medium text-ink">{label}</p>
      <p className="mono-meta tabular text-muted-foreground">{n} response{n === 1 ? '' : 's'}</p>
    </div>
  );
}

function SingleChart({ q }: { q: Extract<QResult, { kind: 'single' }> }): JSX.Element {
  const data = q.options.map((o) => ({ name: o.label, count: o.count }));
  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} accessibilityLayer={false}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip content={<ChartTooltip />} />
          <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {q.blank > 0 && (
        <p className="mono-meta text-muted-foreground">{q.blank} blank / skipped</p>
      )}
    </div>
  );
}

function MultiChart({ q }: { q: Extract<QResult, { kind: 'multi' }> }): JSX.Element {
  const data = q.options.map((o) => ({ name: o.label, count: o.count }));
  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }} accessibilityLayer={false}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
          <Tooltip content={<ChartTooltip />} />
          <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <p className="mono-meta text-muted-foreground">Selections may exceed total responses</p>
    </div>
  );
}

function NumberStats({ q }: { q: Extract<QResult, { kind: 'number' }> }): JSX.Element {
  // Two swipeable cards: the Total (sum of every answer — the headline figure
  // for a count question) on its own, then the spread (responses/avg/min/max).
  const [active, setActive] = useState(0);
  const spread = [
    { label: 'Responses', value: String(q.count) },
    { label: 'Average', value: q.avg != null ? q.avg.toFixed(1) : '—' },
    { label: 'Min', value: q.min != null ? String(q.min) : '—' },
    { label: 'Max', value: q.max != null ? String(q.max) : '—' },
  ];
  return (
    <div className="space-y-2.5">
      <div
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-2 sm:overflow-visible"
        onScroll={(e) => setActive(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
      >
        <div className="card-paper snap-center shrink-0 basis-full border-primary/40 bg-primary/[0.06] p-6 text-center">
          <p className="status-label">Total</p>
          <p className="mt-1 text-4xl font-bold text-primary tabular">{q.sum.toLocaleString()}</p>
          <p className="mono-meta text-muted-foreground mt-1.5">across {q.count.toLocaleString()} response{q.count === 1 ? '' : 's'}</p>
        </div>
        <div className="card-paper snap-center shrink-0 basis-full p-5">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {spread.map(({ label, value }) => (
              <div key={label} className="text-center">
                <p className="status-label">{label}</p>
                <p className="mt-1 text-2xl font-bold text-foreground tabular">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center gap-1.5 sm:hidden" aria-hidden>
        {[0, 1].map((i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${active === i ? 'w-5 bg-primary' : 'w-1.5 bg-border-strong'}`}
          />
        ))}
      </div>
    </div>
  );
}

function CountGroupStats({ q }: { q: Extract<QResult, { kind: 'countgroup' }> }): JSX.Element {
  const [active, setActive] = useState(0);
  const data = q.categories.map((c) => ({ name: c.label, count: c.total }));
  return (
    <div className="space-y-2.5">
      <div
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-2 sm:overflow-visible"
        onScroll={(e) => setActive(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
      >
        <div className="card-paper snap-center shrink-0 basis-full border-primary/40 bg-primary/[0.06] p-6 text-center">
          <p className="status-label">Total</p>
          <p className="mt-1 text-4xl font-bold text-primary tabular">{q.grandTotal.toLocaleString()}</p>
          <p className="mono-meta text-muted-foreground mt-1.5">across {q.responses.toLocaleString()} response{q.responses === 1 ? '' : 's'}</p>
        </div>
        <div className="card-paper snap-center shrink-0 basis-full p-4">
          <ResponsiveContainer width="100%" height={Math.max(140, q.categories.length * 56)}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} accessibilityLayer={false}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex justify-center gap-1.5 sm:hidden" aria-hidden>
        {[0, 1].map((i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all ${active === i ? 'w-5 bg-primary' : 'w-1.5 bg-border-strong'}`} />
        ))}
      </div>
    </div>
  );
}

function TextAnswers({ q }: { q: Extract<QResult, { kind: 'text' }> }): JSX.Element {
  if (q.answers.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No answers yet</p>;
  }
  return (
    <ul className="max-h-60 overflow-y-auto space-y-1.5 pr-1">
      {q.answers.map((ans, i) => (
        <li key={i} className="rounded-md border border-border bg-background px-3 py-2 text-sm">
          {ans}
        </li>
      ))}
    </ul>
  );
}

function DateChart({ q }: { q: Extract<QResult, { kind: 'date' }> }): JSX.Element {
  const data = q.values.map((v) => ({ name: v.date, count: v.count }));
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground italic">No date responses yet</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} accessibilityLayer={false}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PriorityStats({ q }: { q: Extract<QResult, { kind: 'priority' }> }): JSX.Element {
  const n = q.items.length;
  if (q.responses === 0) {
    return <p className="text-sm text-muted-foreground italic">No rankings yet</p>;
  }
  return (
    <div className="space-y-2.5">
      <ol className="space-y-2">
        {q.items.map((it, i) => {
          // Longer bar = higher priority (lower average rank).
          const frac = it.avgRank === null ? 0 : Math.max(0.04, (n + 1 - it.avgRank) / n);
          return (
            <li key={it.label} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-foreground">
                  <span className="mono-meta text-muted-foreground">{i + 1}.</span> {it.label}
                </span>
                <span className="mono-meta tabular text-muted-foreground">
                  {it.avgRank === null ? '—' : `avg ${it.avgRank.toFixed(2)}`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${frac * 100}%` }} />
              </div>
            </li>
          );
        })}
      </ol>
      <p className="mono-meta text-muted-foreground">
        {q.responses} response{q.responses === 1 ? '' : 's'} · lower average = higher priority
      </p>
    </div>
  );
}

function QuestionCard({ q }: { q: QResult }): JSX.Element {
  return (
    <li className="card-paper p-5 space-y-4">
      <h2 className="font-semibold leading-snug text-foreground">{q.label}</h2>
      {q.kind === 'single' && <SingleChart q={q} />}
      {q.kind === 'multi' && <MultiChart q={q} />}
      {q.kind === 'number' && <NumberStats q={q} />}
      {q.kind === 'text' && <TextAnswers q={q} />}
      {q.kind === 'countgroup' && <CountGroupStats q={q} />}
      {q.kind === 'date' && <DateChart q={q} />}
      {q.kind === 'priority' && <PriorityStats q={q} />}
    </li>
  );
}

function ResponseCard({ row, index, surveyId }: { row: ResponseRow; index: number; surveyId: string }): JSX.Element {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => surveysApi.deleteResponse(surveyId, row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['survey-responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['survey-results', surveyId] });
    },
  });
  const preview = previewAnswers(row.answers);
  return (
    <li className="card-paper flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
      <span className="mono-meta flex-none text-muted-foreground">#{index + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{row.submittedByName}</p>
        <p className="mono-meta text-muted-foreground">{fmtMvt(row.submittedAt)}</p>
        {preview && <p className="mt-1 truncate text-sm text-muted-foreground">{preview}</p>}
      </div>
      <div className="flex flex-none items-center gap-2">
        <Link
          to="/surveys/$surveyId/responses/$rid/edit"
          params={{ surveyId, rid: row.id }}
          className="btn-secondary"
        >
          <Icon name="edit" size={15} /> Edit
        </Link>
        <button
          type="button"
          onClick={() => { if (window.confirm('Delete this response permanently?')) del.mutate(); }}
          disabled={del.isPending}
          className="btn-ghost text-destructive"
          aria-label={`Delete response ${index + 1}`}
        >
          <Icon name="delete" size={15} />
        </button>
      </div>
      {del.isError && (
        <p className="basis-full text-sm text-destructive" role="alert">Couldn’t delete this response.</p>
      )}
    </li>
  );
}

function ResponsesTab({ surveyId }: { surveyId: string }): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['survey-responses', surveyId],
    queryFn: () => surveysApi.listResponses(surveyId),
  });

  if (isLoading) {
    return <p className="status-label text-muted-foreground" role="status">Loading…</p>;
  }
  if (isError) {
    return <p className="text-sm text-destructive" role="alert">Failed to load responses. Please try again.</p>;
  }
  if (!data || data.length === 0) {
    return (
      <div className="card-paper p-8 text-center">
        <p className="font-medium">No responses yet</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {data.map((row, i) => (
        <ResponseCard key={row.id} row={row} index={i} surveyId={surveyId} />
      ))}
    </ul>
  );
}

export function SurveyResults(): JSX.Element {
  const { surveyId } = useParams({ from: '/surveys/$surveyId/results' });
  const navigate = useNavigate();
  // Tab lives in the URL (?tab=responses) so it survives navigating to a
  // response's edit page and back.
  const { tab: tabParam } = useSearch({ from: '/surveys/$surveyId/results' });
  const tab: 'summary' | 'responses' = tabParam === 'responses' ? 'responses' : 'summary';
  const setTab = (next: 'summary' | 'responses'): void => {
    void navigate({
      to: '/surveys/$surveyId/results',
      params: { surveyId },
      search: { tab: next === 'responses' ? 'responses' : undefined },
      replace: true,
    });
  };
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['survey-results', surveyId],
    queryFn: () => surveysApi.results(surveyId),
  });

  const downloadM = useMutation({ mutationFn: () => surveysApi.downloadExport(surveyId) });

  const reportM = useMutation({ mutationFn: () => surveysApi.downloadReport(surveyId) });

  const clearM = useMutation({
    mutationFn: () => surveysApi.clearResponses(surveyId),
    onSuccess: () => {
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: ['survey-results', surveyId] });
    },
  });

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {data ? (
            <>
              <h1 className="font-serif text-3xl tracking-tight">{data.survey.title}</h1>
              <p className="mono-meta text-muted-foreground mt-1">
                {STATUS_LABEL[data.survey.status]} · {data.totalResponses} response{data.totalResponses !== 1 ? 's' : ''}
              </p>
            </>
          ) : (
            <h1 className="font-serif text-3xl tracking-tight">Survey Results</h1>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {downloadM.isError && (
            <span className="text-sm text-destructive" role="alert">Download failed</span>
          )}
          {reportM.isError && (
            <span className="text-sm text-destructive" role="alert">Report failed</span>
          )}
          {clearM.isError && (
            <span className="text-sm text-destructive" role="alert">Couldn’t clear responses</span>
          )}
          {data && data.totalResponses > 0 && (
            confirming ? (
              <div className="flex items-center gap-2 rounded-2xl bg-[hsl(var(--error-container))] px-3 py-1.5 text-[hsl(var(--on-error-container))]">
                <span className="text-sm">
                  Delete all {data.totalResponses} response{data.totalResponses !== 1 ? 's' : ''} permanently?
                </span>
                <button
                  type="button"
                  onClick={() => clearM.mutate()}
                  disabled={clearM.isPending}
                  className="btn-primary !bg-destructive !text-destructive-foreground"
                >
                  {clearM.isPending ? 'Clearing…' : 'Yes, clear'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={clearM.isPending}
                  className="btn-ghost"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="btn-secondary text-destructive"
              >
                <Icon name="delete" size={15} /> Clear responses
              </button>
            )
          )}
          {data && data.totalResponses > 0 && (
            <button
              type="button"
              onClick={() => reportM.mutate()}
              disabled={reportM.isPending}
              className="btn-primary"
            >
              <Icon name="auto_awesome" size={15} /> {reportM.isPending ? 'Generating…' : 'Generate AI report'}
            </button>
          )}
          {data && (
            <button
              type="button"
              onClick={() => downloadM.mutate()}
              disabled={downloadM.isPending}
              className="btn-secondary"
            >
              <Icon name="download" size={15} /> {downloadM.isPending ? 'Preparing…' : 'Download Excel'}
            </button>
          )}
          <Link
            to="/surveys/$surveyId"
            params={{ surveyId }}
            className="btn-ghost"
          >
            ← Back to survey
          </Link>
        </div>
      </header>

      <div className="flex gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('summary')}
          aria-pressed={tab === 'summary'}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            tab === 'summary' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Summary
        </button>
        <button
          type="button"
          onClick={() => setTab('responses')}
          aria-pressed={tab === 'responses'}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            tab === 'responses' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Responses
        </button>
      </div>

      {tab === 'summary' ? (
        <>
          {isLoading && (
            <p className="status-label text-muted-foreground" role="status">Loading…</p>
          )}

          {isError && (
            <p className="text-sm text-destructive" role="alert">Failed to load results. Please try again.</p>
          )}

          {data && data.totalResponses === 0 && (
            <div className="card-paper p-8 text-center space-y-2">
              <p className="font-medium">No responses yet</p>
              <p className="text-sm text-muted-foreground">Share the collect link to start gathering responses.</p>
              <Link
                to="/surveys/$surveyId/collect"
                params={{ surveyId }}
                className="btn-primary mt-3 inline-flex"
              >
                Go to collect
              </Link>
            </div>
          )}

          {data && data.totalResponses > 0 && (
            <ul className="space-y-4">
              {data.questions.map((q) => (
                <QuestionCard key={q.id} q={q} />
              ))}
            </ul>
          )}
        </>
      ) : (
        <ResponsesTab surveyId={surveyId} />
      )}
    </div>
  );
}
